import { useEffect, useState } from "react";
import { auth as authApi } from "@/lib/api";
import { useAuth, formatAuthError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, Trash2, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import ImportCustomerHistory from "@/components/ImportCustomerHistory";

export default function Admins() {
  const { user, logout } = useAuth();
  const isSuper = (user?.role || "super") === "super";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "office" });
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });

  async function load() {
    try {
      setItems(await authApi.listAdmins());
    } catch (e) {
      toast.error(formatAuthError(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!form.email || form.password.length < 8) {
      toast.error("Email wajib & password min 8 karakter"); return;
    }
    try {
      await authApi.createAdmin(form);
      toast.success(`Admin ${form.email} ditambahkan`);
      setOpen(false); setForm({ email:"", password:"", name:"", role:"office" });
      load();
    } catch (e) { toast.error(formatAuthError(e)); }
  }

  async function toggleRole(a) {
    const newRole = (a.role === "super") ? "office" : "super";
    const label = newRole === "super" ? "Super Admin" : "Admin Kantor";
    if (!window.confirm(`Ubah role ${a.email} menjadi ${label}?`)) return;
    try {
      await authApi.updateAdmin(a.id, { role: newRole });
      toast.success(`Role diubah menjadi ${label}`);
      load();
    } catch (e) { toast.error(formatAuthError(e)); }
  }

  async function del(a) {
    if (!window.confirm(`Hapus admin ${a.email}?`)) return;
    try {
      await authApi.deleteAdmin(a.id);
      toast.success("Admin dihapus");
      load();
    } catch (e) { toast.error(formatAuthError(e)); }
  }

  async function changePw() {
    if (pwForm.new_password.length < 8) { toast.error("Password baru min 8 karakter"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    try {
      await authApi.changePassword(pwForm.current_password, pwForm.new_password);
      toast.success("Password berhasil diubah — silakan login ulang");
      setPwOpen(false); setPwForm({ current_password:"", new_password:"", confirm:"" });
      logout();
    } catch (e) { toast.error(formatAuthError(e)); }
  }

  return (
    <div className="space-y-4" data-testid="admins-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[12px] text-zinc-500">
          Total: <b className="font-mono text-zinc-800">{items.length}</b> admin
        </div>
        <div className="flex gap-2">
          <Dialog open={pwOpen} onOpenChange={setPwOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm gap-1.5" data-testid="change-password-btn">
                <KeyRound className="w-4 h-4" />Ganti Password Saya
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Ganti Password</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input type="password" placeholder="Password lama" value={pwForm.current_password}
                  onChange={e=>setPwForm({...pwForm, current_password:e.target.value})}
                  className="rounded-sm" data-testid="pw-current-input" />
                <Input type="password" placeholder="Password baru (min 8 karakter)" value={pwForm.new_password}
                  onChange={e=>setPwForm({...pwForm, new_password:e.target.value})}
                  className="rounded-sm" data-testid="pw-new-input" />
                <Input type="password" placeholder="Ulangi password baru" value={pwForm.confirm}
                  onChange={e=>setPwForm({...pwForm, confirm:e.target.value})}
                  className="rounded-sm" data-testid="pw-confirm-input" />
                <Button onClick={changePw} className="w-full bg-[#E65100] hover:bg-[#F57C00] rounded-sm" data-testid="pw-save-btn">
                  Simpan & Logout
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5" data-testid="add-admin-btn" disabled={!isSuper}
                title={isSuper ? "" : "Hanya Super Admin yang dapat menambah admin"}>
                <UserPlus className="w-4 h-4" />Tambah Admin
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Admin Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Nama (opsional)" value={form.name}
                  onChange={e=>setForm({...form, name:e.target.value})} className="rounded-sm" data-testid="admin-name-input" />
                <Input type="email" placeholder="Email" value={form.email}
                  onChange={e=>setForm({...form, email:e.target.value})} className="rounded-sm" data-testid="admin-email-input" />
                <Input type="password" placeholder="Password (min 8 karakter)" value={form.password}
                  onChange={e=>setForm({...form, password:e.target.value})} className="rounded-sm" data-testid="admin-password-input" />
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Role</label>
                  <select value={form.role}
                    onChange={e=>setForm({...form, role:e.target.value})}
                    className="mt-1 w-full h-9 rounded-sm border border-border bg-white px-2 text-[13px]"
                    data-testid="admin-role-select">
                    <option value="office">Admin Kantor (terbatas)</option>
                    <option value="super">Super Admin (akses penuh)</option>
                  </select>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Admin Kantor tidak dapat melihat menu <b>Pengaturan</b> &amp; <b>Admin</b>.
                  </div>
                </div>
                <Button onClick={create} className="w-full bg-[#E65100] hover:bg-[#F57C00] rounded-sm" data-testid="admin-save-btn">Simpan</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map(a => {
          const role = a.role || "super";
          const isSelf = a.id === user?.id;
          return (
          <div key={a.id} className="border border-border bg-white rounded-sm p-4" data-testid={`admin-card-${a.id}`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full grid place-items-center text-white ${role === "super" ? "bg-[#E65100]" : "bg-zinc-500"}`}>
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {a.name || a.email.split("@")[0]}
                  {isSelf && <span className="text-[9px] bg-emerald-100 text-emerald-700 rounded-sm px-1.5 py-0.5 uppercase tracking-widest">Anda</span>}
                  <span
                    className={`text-[9px] rounded-sm px-1.5 py-0.5 uppercase tracking-widest font-bold ${role === "super" ? "bg-[#E65100]/10 text-[#E65100]" : "bg-zinc-100 text-zinc-600"}`}
                    data-testid={`admin-role-badge-${a.id}`}
                  >
                    {role === "super" ? "Super Admin" : "Admin Kantor"}
                  </span>
                </div>
                <div className="text-[11px] text-zinc-500">{a.email}</div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  Dibuat: {a.created_at?.slice(0,10) || "—"}
                  {a.created_by && a.created_by !== "system" && ` · oleh ${a.created_by}`}
                  {a.created_by === "system" && " · seed"}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                {isSuper && !isSelf && (
                  <Button size="icon" variant="ghost" onClick={()=>toggleRole(a)}
                    title={role === "super" ? "Turunkan ke Admin Kantor" : "Naikkan ke Super Admin"}
                    className="h-7 w-7 rounded-sm text-zinc-400 hover:text-[#E65100]" data-testid={`toggle-role-${a.id}`}>
                    <ShieldCheck className="w-3.5 h-3.5" />
                  </Button>
                )}
                {isSuper && !isSelf && (
                  <Button size="icon" variant="ghost" onClick={()=>del(a)}
                    className="h-7 w-7 rounded-sm text-zinc-400 hover:text-red-500" data-testid={`delete-admin-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        );})}
      </div>

      {isSuper && <ImportCustomerHistory />}
    </div>
  );
}
